import { createSelector } from '@reduxjs/toolkit';
import { useEffect, useMemo, useRef } from 'react';
import type { MapRef } from 'react-map-gl/mapbox';
import { useDispatch, useSelector } from 'react-redux';
import type SearchOrigin from '@alltrails/amplitude/enums/SearchOrigin';
import logExploreFiltersApplied from '@alltrails/amplitude/events/logExploreFiltersApplied';
import logSearchResultClicked from '@alltrails/amplitude/events/logSearchResultClicked';
import useDebouncedValue from '@alltrails/core/hooks/useDebouncedValue';
import useExperiments from '@alltrails/experiments/hooks/useExperiments';
import type Context from '@alltrails/types/Context';
import type { LocaleUnits } from '@alltrails/types/LocaleUnits';
import useGeneralExploreServiceQueryArgs from '../hooks/useGeneralExploreServiceQueryArgs';
import { useGetResultsQuery } from '../redux/exploreServiceApi';
import {
  ExploreServiceState,
  updateFilters,
  updateNaturalLanguageSearchAnalyticData,
  updateNaturalLanguageSearchIsActive,
  updateNaturalLanguageSearchSummary,
  updateSearchIsLoading,
  updateSearchResults,
  updateSearchResultsSummary,
  updateSort
} from '../redux/exploreServiceSlice';
import ExploreEventData from '../types/ExploreEventData';
import buildExploreFiltersAppliedParams, { isDefaultFilterState } from '../utils/buildExploreFiltersAppliedParams';
import getExploreServiceViewport from '../utils/getExploreServiceViewport';

type ExploreFiltersAppliedStateForAnalytics = {
  boundingBox: ExploreServiceState['boundingBox'];
  distanceAway: ExploreServiceState['distanceAway'];
  filters: ExploreServiceState['filters'];
  searchResultsSummary: ExploreServiceState['searchResultsSummary'];
  sort: ExploreServiceState['sort'];
  trailEngagement: ExploreServiceState['trailEngagement'];
  useMetric: boolean;
  searchOrigin: SearchOrigin | undefined;
};

type StateWithContext = { context: Context; exploreService: ExploreServiceState };

const selectExploreFiltersAppliedStateForAnalytics = createSelector(
  [
    (state: StateWithContext) => state.exploreService.boundingBox,
    (state: StateWithContext) => state.exploreService.distanceAway,
    (state: StateWithContext) => state.exploreService.filters,
    (state: StateWithContext) => state.exploreService.searchResultsSummary,
    (state: StateWithContext) => state.exploreService.sort,
    (state: StateWithContext) => state.exploreService.trailEngagement,
    (state: StateWithContext) => state.context.displayMetric,
    (state: StateWithContext) => state.exploreService.naturalLanguageSearchAnalyticData?.searchOrigin
  ],
  (
    boundingBox,
    distanceAway,
    filters,
    searchResultsSummary,
    sort,
    trailEngagement,
    useMetric,
    searchOrigin
  ): ExploreFiltersAppliedStateForAnalytics => ({
    boundingBox,
    distanceAway,
    filters,
    searchResultsSummary,
    sort,
    trailEngagement,
    useMetric,
    searchOrigin
  })
);

type Args = Parameters<typeof useGeneralExploreServiceQueryArgs>[0];

export default function useExploreServiceResults(map?: MapRef, args: Args = {}) {
  const dispatch = useDispatch();
  const displayMetric = useSelector((state: { context: Context }) => state.context.displayMetric);
  const {
    allFiltersModalOpen,
    distanceAwayLoading,
    naturalLanguageQuery,
    naturalLanguageSearchAnalyticData,
    naturalLanguageSearchIsActive,
    skipQuery,
    userLocation
  } = useSelector((state: { exploreService: ExploreServiceState }) => state.exploreService);
  const exploreFiltersAppliedStateForAnalytics = useSelector(selectExploreFiltersAppliedStateForAnalytics);
  const experiments = useExperiments();
  const includePlaceInNLS = experiments['web-discover-rm-nls-improvements']?.value === 'treatment';

  const lastFilterStateWhenResultsReceived = useRef<{
    distanceAway: ExploreServiceState['distanceAway'];
    filters: ExploreServiceState['filters'];
    sort: ExploreServiceState['sort'];
    trailEngagement: ExploreServiceState['trailEngagement'];
  } | null>(null);
  const triggeredNaturalLanguageQuery = useRef('');
  const exploreQueryArgs = useGeneralExploreServiceQueryArgs(args);
  const argsForNaturalLanguageSearch = useMemo(() => {
    const localeUnits: LocaleUnits = displayMetric ? 'metric' : 'imperial';
    const triggerNaturalLanguageSearch = Boolean(naturalLanguageQuery && naturalLanguageQuery !== triggeredNaturalLanguageQuery.current);
    if (triggerNaturalLanguageSearch) {
      const mapCenter = map?.getCenter();
      const viewport = getExploreServiceViewport(map);

      return {
        ...exploreQueryArgs,
        // Send the center rather than the bounding box since the explore-service will not generate a new bounding box is one is sent in the request
        ...(mapCenter ? { location: { center: { lat: mapCenter.lat, lng: mapCenter.lng } } } : {}),
        ...(viewport ? { context: { userLocation, viewport } } : {}),
        localeUnits,
        triggerNaturalLanguageSearch,
        ...(includePlaceInNLS ? { includePlaceInNLS: true } : {})
      };
    }
    return { ...exploreQueryArgs, localeUnits };
  }, [displayMetric, exploreQueryArgs, includePlaceInNLS, map, naturalLanguageQuery, userLocation]);
  const debouncedExploreQueryArgs = useDebouncedValue(argsForNaturalLanguageSearch, 100);

  const {
    data: exploreData,
    isFetching: isFetchingExplore,
    isLoading: isLoadingExplore
  } = useGetResultsQuery(debouncedExploreQueryArgs, {
    skip:
      naturalLanguageSearchIsActive || // Skip additional searches until the natural language search is inactive
      skipQuery ||
      ('bottomLeft' in debouncedExploreQueryArgs.location &&
        (debouncedExploreQueryArgs.location.bottomLeft === undefined ||
          debouncedExploreQueryArgs.location.bottomRight === undefined ||
          debouncedExploreQueryArgs.location.topLeft === undefined ||
          debouncedExploreQueryArgs.location.topRight === undefined ||
          debouncedExploreQueryArgs.location.mapRotation === undefined))
  });

  // Hold `isLoading` at true when a place is identified through the redirect period so the sidebar stays in its loading state
  const shouldRedirectToPlace = includePlaceInNLS && exploreData?.place?.type === 'area';
  const isLoading = useMemo(
    () => Boolean(isFetchingExplore || isLoadingExplore || distanceAwayLoading || shouldRedirectToPlace),
    [distanceAwayLoading, isFetchingExplore, isLoadingExplore, shouldRedirectToPlace]
  );

  useEffect(() => {
    dispatch(updateSearchIsLoading(isLoading));
  }, [dispatch, isLoading]);

  // Update results and summary in the same effect so they stay in sync (same API response).
  // When we get new results, log "explore filters applied" only if filters/sort/trailEngagement/distanceAway changed since last results (user applied or cleared a filter).
  useEffect(() => {
    if (exploreData?.summary === undefined) {
      return;
    }

    dispatch(updateSearchResults(exploreData.searchResults ?? []));
    dispatch(updateSearchResultsSummary(exploreData.summary));

    const currentFilterState = {
      distanceAway: exploreFiltersAppliedStateForAnalytics.distanceAway,
      filters: exploreFiltersAppliedStateForAnalytics.filters,
      sort: exploreFiltersAppliedStateForAnalytics.sort,
      trailEngagement: exploreFiltersAppliedStateForAnalytics.trailEngagement
    };
    const prev = lastFilterStateWhenResultsReceived.current;
    const filterStateChanged =
      prev === null ||
      JSON.stringify(prev.filters) !== JSON.stringify(currentFilterState.filters) ||
      prev.sort !== currentFilterState.sort ||
      JSON.stringify(prev.trailEngagement) !== JSON.stringify(currentFilterState.trailEngagement) ||
      JSON.stringify(prev.distanceAway) !== JSON.stringify(currentFilterState.distanceAway);

    if (filterStateChanged) {
      lastFilterStateWhenResultsReceived.current = currentFilterState;
      // Log when state is non-default (e.g. filters in URL on load, or user applied via inline chip).
      // Skip while All Filters modal is open (filter bar logs once on "See trails" button click).
      if (!allFiltersModalOpen && !isDefaultFilterState(currentFilterState)) {
        const stateWithFreshSummary = {
          ...exploreFiltersAppliedStateForAnalytics,
          searchResultsSummary: exploreData.summary
        };
        logExploreFiltersApplied(buildExploreFiltersAppliedParams(stateWithFreshSummary));
      }
    }
    // Only run when we get new API data; compare to ref to decide if user had changed filters/sort/trailEngagement/distanceAway.
  }, [exploreData?.searchResults, exploreData?.summary]);

  useEffect(() => {
    if (exploreData === undefined) {
      return;
    }

    dispatch(updateNaturalLanguageSearchSummary(exploreData.hitSummaryText));
  }, [dispatch, exploreData, exploreData?.hitSummaryText]);

  // Handle data from natural language search response
  useEffect(() => {
    if (!exploreData?.searchConfig || !exploreData?.boundingBox) {
      return;
    }

    dispatch(updateNaturalLanguageSearchIsActive(true));

    // The analytics data requires the queryId which we get back from the explore-service,
    // but the other data is populated before the request is even sent, so we need to update it here.
    // Now that we have the queryId, we can log the "search result clicked" event for the nls result.
    if (naturalLanguageSearchAnalyticData && !naturalLanguageSearchAnalyticData.queryId && exploreData?.queryId) {
      dispatch(updateNaturalLanguageSearchAnalyticData({ ...naturalLanguageSearchAnalyticData, queryId: exploreData.queryId }));

      if (naturalLanguageSearchAnalyticData.searchOrigin && naturalLanguageSearchAnalyticData.selectionType) {
        logSearchResultClicked({
          click_position: naturalLanguageSearchAnalyticData.clickPosition,
          location: naturalLanguageSearchAnalyticData.searchOrigin,
          query_id: exploreData.queryId,
          query_length: naturalLanguageSearchAnalyticData.queryText?.length,
          query_text: naturalLanguageSearchAnalyticData.queryText,
          selection_method: naturalLanguageSearchAnalyticData.selectionMethod,
          selection_type: naturalLanguageSearchAnalyticData.selectionType
        });
      }
    }

    triggeredNaturalLanguageQuery.current = debouncedExploreQueryArgs.query ?? '';

    // Skip filter/sort dispatches when we're about to redirect to a park/area page — the park page reads filters from the URL params.
    if (shouldRedirectToPlace) {
      return;
    }

    const { filters, sort } = exploreData.searchConfig;
    if (filters) {
      let updatedFilters = filters;
      // Server includes hasTrails on the filters object but the client doesn't treat it as a user-facing filter
      if ('hasTrails' in updatedFilters) {
        // eslint-disable-next-line unused-imports/no-unused-vars -- Intentionally dropping hasTrails
        const { hasTrails, ...validFilters } = updatedFilters;
        updatedFilters = validFilters;
      }
      if (updatedFilters?.activity && updatedFilters?.activity?.length > 1) {
        // If the server returns multiple activity types, only take the first one since the client only handles one
        updatedFilters.activity = updatedFilters.activity.slice(0, 1);
      }
      dispatch(updateFilters(updatedFilters));
    }
    if (sort) {
      dispatch(updateSort(sort));
    }

    const { bottomLeft, topRight } = exploreData.boundingBox;
    const eventData: ExploreEventData = { boundingBox: exploreData.boundingBox, mapRotation: 0, naturalLanguageSearchTriggered: true };
    if (map) {
      map.fitBounds(
        [
          [bottomLeft.lng, bottomLeft.lat],
          [topRight.lng, topRight.lat]
        ],
        {},
        eventData
      );
    }
    // Don't want to run this when the analytic data changes
  }, [dispatch, exploreData, map, debouncedExploreQueryArgs.query, shouldRedirectToPlace]);

  return { exploreData };
}
